import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { cn } from '@/lib/cn'

import type { Expense, ExpenseCategory, I18n } from '../types'

/**
 * Modifier une dépense — et, si elle n'a rien engagé, la retirer.
 *
 * Deux gestes très différents cohabitent ici, et l'écran ne les mélange pas :
 *
 * * **Supprimer** ne s'offre que sur un brouillon ou une dépense en attente :
 *   personne n'a encore tranché. Un brouillon ne figure dans aucun total ;
 *   une dépense en attente demande une confirmation, son montant quittant le
 *   budget consommé.
 * * **Annuler** est la sortie de tout le reste. Le montant cesse d'être compté
 *   — même effet sur les chiffres — mais la ligne demeure avec sa raison,
 *   sans quoi le rapport de clôture deviendrait invérifiable.
 *
 * Passée l'approbation, plus rien ne se modifie : réécrire le montant d'une
 * dépense validée reviendrait à réécrire une décision prise par quelqu'un.
 */
const LOCKED = new Set(['approved', 'paid', 'cancelled'])
const DELETABLE = new Set(['draft', 'submitted'])

export function ExpenseEditor({
  expense,
  categories,
  label,
  busy,
  error,
  onSave,
  onDelete,
  onForceDelete,
  onClose,
}: {
  expense: Expense
  categories: ExpenseCategory[]
  label: (value: I18n) => string
  busy: boolean
  error: string | null
  onSave: (payload: Record<string, unknown>) => void
  onDelete: () => void
  /**
   * Suppression d'une dépense déjà soumise.
   *
   * Proposée **directement**, sans passer par un refus du serveur : l'écran
   * connaît le statut de la ligne, et faire cliquer sur un bouton pour
   * découvrir une erreur rouge puis chercher un second bouton n'est pas un
   * geste, c'est une devinette.
   */
  onForceDelete: () => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const locked = LOCKED.has(expense.status)
  // Seuls le brouillon et la dépense en attente se suppriment : personne n'a
  // encore tranché. Approuvée, payée, refusée ou annulée, la ligne porte une
  // décision — elle s'annule avec un motif, elle ne disparaît plus. Le
  // serveur refuse de toute façon ; ne pas offrir le bouton évite de faire
  // découvrir la règle par une erreur rouge.
  const deletable = DELETABLE.has(expense.status)
  // Une dépense soumise a consommé du budget : l'effacer retire son montant
  // du budget consommé. On le dit dans la confirmation, on ne le fait pas
  // découvrir après.
  const engaged = expense.status === 'submitted'

  const [form, setForm] = useState({
    label: expense.label,
    amount: (expense.amount_minor / 100).toFixed(2),
    category_id: expense.category_id ?? '',
    spent_on: expense.spent_on.slice(0, 10),
    supplier: expense.supplier ?? '',
    description: expense.description ?? '',
  })
  const [confirming, setConfirming] = useState(false)

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/50" role="dialog" aria-modal>
      <button
        type="button"
        aria-label={t('admin.project.close')}
        className="flex-1 cursor-default"
        onClick={onClose}
      />

      <div className="flex w-full max-w-lg flex-col overflow-y-auto border-l border-line bg-bg-base p-6">
        <div className="flex items-start justify-between gap-3">
          <p className="font-mono text-caption text-content-muted">{expense.reference}</p>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t('admin.project.close')}
          </Button>
        </div>

        {locked ? (
          <p className="mt-4 rounded-md border border-line-subtle px-3 py-2 text-sm text-content-secondary">
            {t('admin.finance.lockedHint')}
          </p>
        ) : (
          <div className="mt-5 space-y-4">
            <Field label={t('admin.finance.label')}>
              <input
                value={form.label}
                onChange={(event) => setForm({ ...form, label: event.target.value })}
                className={INPUT}
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('admin.finance.amount')}>
                <input
                  inputMode="decimal"
                  value={form.amount}
                  onChange={(event) => setForm({ ...form, amount: event.target.value })}
                  className={cn(INPUT, 'tabular-nums')}
                />
              </Field>

              <Field label={t('admin.finance.date')}>
                <input
                  type="date"
                  value={form.spent_on}
                  onChange={(event) => setForm({ ...form, spent_on: event.target.value })}
                  className={INPUT}
                />
              </Field>

              <Field label={t('admin.project.category')}>
                <select
                  value={form.category_id}
                  onChange={(event) => setForm({ ...form, category_id: event.target.value })}
                  className={INPUT}
                >
                  <option value="">{t('admin.project.noCategory')}</option>
                  {categories.map((row) => (
                    <option key={row.id} value={row.id}>
                      {label(row.name_i18n)}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label={t('admin.finance.supplier')}>
                <input
                  value={form.supplier}
                  onChange={(event) => setForm({ ...form, supplier: event.target.value })}
                  className={INPUT}
                />
              </Field>
            </div>

            <Field label={t('admin.food.descriptionField')}>
              <textarea
                rows={3}
                value={form.description}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
                className={cn(INPUT, 'h-auto py-2')}
              />
            </Field>
          </div>
        )}

        {/* Le journal des décisions : qui, quand, et pourquoi. C'est ce qu'on
            vient lire trois semaines plus tard, devant un fournisseur. */}
        <ExpenseTrail expense={expense} />

        {error ? <p className="mt-4 text-sm text-ember-300">{error}</p> : null}

        {locked ? null : (
          <div className="mt-6 flex flex-wrap gap-3">
            <Button
              disabled={busy || !form.label.trim()}
              onClick={() => {
                const amount = Number.parseFloat(form.amount.replace(',', '.'))
                if (!Number.isFinite(amount) || amount <= 0) return
                onSave({
                  label: form.label.trim(),
                  // Le serveur ne manipule que des unités mineures : un centime
                  // perdu dans un flottant se retrouve dans le bénéfice.
                  amount_minor: Math.round(amount * 100),
                  category_id: form.category_id || null,
                  spent_on: form.spent_on,
                  supplier: form.supplier.trim() || null,
                  description: form.description.trim() || null,
                })
              }}
            >
              {t('admin.project.save')}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              {t('admin.project.cancel')}
            </Button>
          </div>
        )}

        {deletable ? (
          <div className="mt-8 border-t border-line-subtle pt-5">
            {confirming ? (
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-sm text-content-secondary">
                  {engaged
                    ? t('admin.finance.deleteEngagedConfirm')
                    : t('admin.finance.deleteConfirm')}
                </p>
                <Button
                  variant={engaged ? 'ember' : 'champagne'}
                  size="sm"
                  disabled={busy}
                  onClick={engaged ? onForceDelete : onDelete}
                >
                  {t('admin.project.deleteYes')}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                  {t('admin.project.cancel')}
                </Button>
              </div>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => setConfirming(true)}>
                  {t('admin.finance.delete')}
                </Button>
                <p className="mt-2 text-caption text-content-muted">
                  {t('admin.finance.deleteHint')}
                </p>
              </>
            )}
          </div>
        ) : (
          <p className="mt-8 border-t border-line-subtle pt-5 text-caption text-content-muted">
            {t('admin.finance.cancelInsteadHint')}
          </p>
        )}
      </div>
    </div>
  )
}

/** Le fil des décisions, dans l'ordre où elles ont été prises. */
export function ExpenseTrail({ expense }: { expense: Expense }) {
  const { t, i18n } = useTranslation()

  const when = (value: string | null) =>
    value ? new Date(value).toLocaleString(i18n.language) : null

  const steps: { key: string; at: string | null; who: string | null; why: string | null }[] = [
    {
      key: 'created',
      at: when(expense.created_at),
      who: expense.created_by_label,
      why: null,
    },
    { key: 'submitted', at: when(expense.submitted_at), who: null, why: null },
    {
      key: 'approved',
      at: when(expense.approved_at),
      who: expense.approved_by_label,
      why: null,
    },
    {
      key: 'rejected',
      at: when(expense.rejected_at),
      who: expense.rejected_by_label,
      why: expense.rejected_reason,
    },
    { key: 'paid', at: when(expense.paid_at), who: expense.paid_by_label, why: null },
    {
      key: 'cancelled',
      at: when(expense.cancelled_at),
      who: expense.cancelled_by_label,
      why: expense.cancel_reason,
    },
  ].filter((step) => step.at !== null)

  return (
    <div className="mt-8">
      <p className="overline text-content-muted">{t('admin.finance.trail')}</p>
      <ul className="mt-3 space-y-2.5">
        {steps.map((step) => (
          <li key={step.key} className="border-l-2 border-l-line pl-3">
            <p className="text-sm text-content-primary">
              {t(`admin.finance.trailSteps.${step.key}`)}
              {step.who ? (
                <span className="text-content-secondary"> — {step.who}</span>
              ) : null}
            </p>
            <p className="text-caption text-content-muted">{step.at}</p>
            {step.why ? (
              <p className="mt-1 text-sm text-champagne-300">« {step.why} »</p>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  )
}

const INPUT =
  'mt-1 h-11 w-full rounded-md border border-line bg-bg-elevated px-3 text-sm text-content-primary'

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-caption text-content-muted">
      {label}
      {children}
    </label>
  )
}
